import type { useHistory } from 'react-router-dom';

type History = ReturnType<typeof useHistory>;

/**
 * Follow the deep link stored on a notification (`url` column).
 *
 * Notification links reach us in three shapes, because they are written by
 * several producers: app-relative paths from the dashboard itself, absolute
 * URLs built server-side against `web_server_url_v2` (e.g. the collection
 * view link on a review task), and occasionally a genuinely external URL.
 * All three have to end up somewhere sensible without a full page reload
 * where one can be avoided.
 *
 * Shared by the Notifications page and Assigned to me so the two cannot drift.
 */
export const openNotificationUrl = (history: History, url: string): void => {
  if (!url) return;

  // App-relative path: navigate within the SPA.
  if (url.startsWith('/')) {
    history.push(url);
    return;
  }

  // Absolute URL: if it points back at this app host, strip the origin and
  // navigate in-app (avoids a full page reload / new tab). We compare host
  // (hostname + port) rather than origin so a protocol mismatch between the
  // stored URL and the running app (e.g. https:// link vs http://localhost)
  // still resolves to in-app navigation. Otherwise open externally.
  try {
    const target = new URL(url, window.location.origin);
    if (target.host === window.location.host) {
      history.push(`${target.pathname}${target.search}${target.hash}`);
      return;
    }
    window.open(target.href, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Recover the review's own subject from a task notification's message.
 *
 * `NotificationManager._notify_in_account_recipients`
 * (`filespin/core/notifications.py`) composes the message as
 * `"{assigner_email} assigned you: {subject}"`. That marker is a deliberate
 * contract — the backend itself splits on it to recover the subject when it
 * tells the assigner the outcome — so reading it here is supported, not a
 * hack. `assigned_by_email` carries the assigner separately, which is why
 * the prefix is redundant once we show it as its own field.
 *
 * Falls back to the whole message for any notification that does not carry
 * the marker.
 */
const ASSIGNED_MARKER = ' assigned you: ';

export const taskSubject = (message: string): string => {
  const at = message.indexOf(ASSIGNED_MARKER);
  if (at === -1) return message;
  return message.slice(at + ASSIGNED_MARKER.length).trim() || message;
};
